import { z } from 'zod';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
    type StoredJsonRowBoundary,
} from '../../stored-json-contract.types';
import {
    type AchPaymentGatewayLifecycleRequest,
    type BinLookupGatewayLifecycleRequest,
    type BinLookupEvidenceV1,
    type AchPaymentGatewayRequest,
    type CardPointeCnpTokenSaleGatewayLifecycleRequest,
    type CardPointeCnpTokenSaleRequestBody,
    type CardPointeCnpTokenSaleRequestDetails,
    CardPointeLifecycleFinalizationGatewayOperation,
    type CardPointeLifecycleFinalizationGatewayLifecycleRequest,
    type CardPointeLifecycleFinalizationRequestDetails,
    type CardPointeLifecycleFinalizationRequestPayload,
    CardPointeLifecyclePreflightGatewayOperation,
    type CardPointeLifecyclePreflightGatewayLifecycleRequest,
    type CardPointeLifecyclePreflightRequestDetails,
    type CardPointeLifecyclePreflightRequestPayload,
    type CardPointeFundingRequestDetails,
    type CardPointeFundingRequestQuery,
    type CardPointeReconciliationGatewayLifecycleRequest,
    type CardPointeReconciliationLookupRequest,
    type CardPointeReconciliationLookupRequestDetails,
    type CardPointeRefundRequestBody,
    type CardPointeRefundRequestDetails,
    type CardPointeRefundGatewayLifecycleRequest,
    type CardPointeStoredCardSaleGatewayLifecycleRequest,
    type CardPointeTerminalSaleAuthorizationBody,
    type CardPointeTerminalSaleErrorBody,
    type CardPointeTerminalSaleGatewayLifecycleRequest,
    type CardPointeTerminalSaleLifecyclePayload,
    CardPointeTerminalSaleLifecyclePayloadType,
    CardPointeTerminalSaleOutcome,
    type CardPointeTerminalSaleRequestBody,
    type CardPointeTerminalSaleRequestDetails,
    type CardPointeTerminalSaleRequestLifecyclePayload,
    type CardPointeTerminalSaleResponseDetails,
    type CardPointeTerminalSaleResponseLifecyclePayload,
    type CardPointeTerminalTokenizationRequestBody,
    type CardPointeTerminalTokenizationRequestDetails,
    type CardPointeTerminalTokenizationGatewayLifecycleRequest,
    type CardPointeTransactionStatusGatewayLifecycleRequest,
    type CardPointeVoidGatewayLifecycleRequest,
    type CardPointeVoidRequestBody,
    type CardPointeVoidRequestDetails,
    type CloverCnpTokenSaleGatewayLifecycleRequest,
    type CloverCnpTokenSaleFirstStoredCredentials,
    type CloverCnpTokenSaleRequestBody,
    type CloverCnpTokenSaleRequestDetails,
    type CloverCnpTokenSaleRequestHeaders,
    type CloverCnpTokenSaleStoredCredentials,
    type CloverCnpTokenSaleSubsequentStoredCredentials,
    CloverLifecycleFinalizationGatewayOperation,
    type CloverLifecycleFinalizationGatewayLifecycleRequest,
    type CloverLifecycleFinalizationRequestDetails,
    type CloverLifecycleFinalizationRequestPayload,
    CloverLifecyclePreflightGatewayOperation,
    type CloverLifecyclePreflightGatewayLifecycleRequest,
    type CloverLifecyclePreflightRequestDetails,
    type CloverLifecyclePreflightRequestPayload,
    type CloverReconciliationGatewayLifecycleRequest,
    type CloverReconciliationLookupRequest,
    type CloverReconciliationLookupRequestDetails,
    type CloverRefundGatewayLifecycleRequest,
    type CloverCloudPayRefundRequestBody,
    type CloverCloudPayRefundRequestDetails,
    type CloverCloudPayRefundRequestHeaders,
    type CloverEcommerceRefundRequestDetails,
    CloverRefundKind,
    type CloverRefundRequestDetails,
    type CloverStoredCardSaleGatewayLifecycleRequest,
    CloverPaymentResult,
    type CloverTerminalSaleCardTransactionBody,
    type CloverTerminalSaleErrorBody,
    type CloverTerminalSaleGatewayLifecycleRequest,
    type CloverTerminalSaleLifecyclePayload,
    CloverTerminalSaleLifecyclePayloadType,
    CloverTerminalSaleOutcome,
    type CloverTerminalSalePaymentBody,
    type CloverTerminalSaleRequestBody,
    type CloverTerminalSaleRequestDetails,
    type CloverTerminalSaleRequestHeaders,
    type CloverTerminalSaleRequestLifecyclePayload,
    type CloverTerminalSaleResponseDetails,
    type CloverTerminalSaleResponseLifecyclePayload,
    type CloverTerminalTokenizationGatewayLifecycleRequest,
    type CloverTransactionDetailsRequestHeaders,
    type CloverTransactionStatusRequestDetails,
    type CloverTransactionStatusGatewayLifecycleRequest,
    type CloverVoidGatewayLifecycleRequest,
    CloverVoidReason,
    type CloverCloudPayVoidRequestDetails,
    type CloverEcommerceVoidRequestDetails,
    type CloverVoidRequestBody,
    type CloverVoidRequestDetails,
    type CloverVoidRequestHeaders,
    GatewayLifecycleRequestType,
    type GatewayLifecycleRequest,
    type IntegrationReconciliationGatewayLifecycleRequest,
    type IntegrationReconciliationGatewayRequest,
    PaymentGatewayTerminalSaleStatus,
    type PaymentGatewayTerminalSaleResult,
    type TerminalSaleDispatchGatewayRequest,
    type TerminalSaleDispatchSourceDocumentKeySnapshot,
    type TerminalSaleDispatchGatewayLifecycleRequest,
    type TerminalSaleDispatchTransactionDetailsSnapshot,
    type ValorAdditionalPrompt,
    type ValorCardTokenSaleGatewayLifecycleRequest,
    type ValorCardTokenSaleGatewayRequest,
    type ValorReconciliationGatewayLifecycleRequest,
    type ValorReconciliationGatewayRequest,
    type ValorRefundGatewayLifecycleRequest,
    type ValorRefundGatewayRequest,
    type ValorRefundPrompt,
    type ValorTerminalPublishGatewayLifecycleRequestV1,
    type ValorTerminalPublishGatewayLifecycleRequestV2,
    type ValorTerminalPublishGatewayRequestV1,
    type ValorTerminalPublishGatewayRequestV2,
    type ValorTerminalPublishPayloadV1,
    type ValorTerminalPublishPayloadV2,
    type ValorTransactionListGatewayLifecycleRequest,
    type ValorTransactionListGatewayRequest,
    type ValorTransactionStatusGatewayLifecycleRequest,
    type ValorTransactionStatusGatewayRequest,
    type ValorVoidGatewayLifecycleRequest,
    type ValorVoidGatewayRequest,
} from '../../types/ipg/gateway-lifecycle-request.types';
import { BinLookupBackend } from '../../backend_config.types';
import { CardType } from '../../payment.types';
import { JsonValueSchema } from '../common.schemas';
import { omitEmptyCloverCardTransactionText } from './clover/clover-card-transaction.schemas';
import {
    normalizeStoredJsonNumberFieldsAtPath,
    normalizeStoredJsonSafeIntegerFieldsAtPath,
    StoredJsonNumberRecordSchema,
} from '../../stored-json-number';

export const CardPointeTransactionStatusRequestPathParamsSchema = z.object({
    orderid: z.string().min(1),
    merchid: z.string().min(1),
    set: z.literal('1'),
});

export const CardPointeTransactionStatusRequestDetailsSchema = z.object({
    method: z.literal('GET'),
    pathTemplate: z.literal(
        '/cardconnect/rest/inquireByOrderid/{orderid}/{merchid}/{set}'
    ),
    pathParams: CardPointeTransactionStatusRequestPathParamsSchema,
});

export const CloverCardEntryMethodSchema = z.enum([
    'MAG_STRIPE',
    'EMV',
    'NFC',
    'MANUAL',
    'DEFAULT',
]);

export const CloverTerminalTokenizationRequestHeadersSchema = z.object({
    deviceId: z.string().min(1),
    idempotencyKey: z.string().min(1).optional(),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
});

export const CloverTerminalTokenizationRequestBodySchema = z.object({
    cardEntryMethods: z.array(CloverCardEntryMethodSchema).optional(),
    tokenType: z.literal('ECOMM_COMPAT'),
});

export const CloverTerminalTokenizationRequestDetailsSchema = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/card/tokens'),
    headers: CloverTerminalTokenizationRequestHeadersSchema,
    body: CloverTerminalTokenizationRequestBodySchema,
});

export const CloverTransactionDetailsRequestHeadersSchema: z.ZodType<CloverTransactionDetailsRequestHeaders> =
    z.object({
        deviceId: z.string().min(1),
        posId: z.string().min(1),
        userAgent: z.string().min(1),
        timeoutSeconds: z.number().int().min(0).max(300).optional(),
    });

export const CloverTransactionStatusRequestDetailsSchema: z.ZodType<CloverTransactionStatusRequestDetails> =
    z.object({
        method: z.literal('GET'),
        path: z.literal('/v1/payments/external/{externalPaymentId}'),
        externalPaymentId: z.string().min(1),
        headers: CloverTransactionDetailsRequestHeadersSchema,
    });

export const CardPointeTerminalSaleAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeTerminalSaleRequestBodySchema: z.ZodObject<{
    merchantId: z.ZodString;
    hsn: z.ZodString;
    amount: z.ZodString;
    capture: z.ZodLiteral<'true'>;
    includeSignature: z.ZodOptional<z.ZodBoolean>;
    beep: z.ZodOptional<z.ZodBoolean>;
    orderId: z.ZodOptional<z.ZodString>;
}> = z.object({
    merchantId: z.string().min(1),
    hsn: z.string().min(1),
    amount: CardPointeTerminalSaleAmountSchema,
    capture: z.literal('true'),
    includeSignature: z.boolean().optional(),
    beep: z.boolean().optional(),
    orderId: z.string().min(1).optional(),
}) satisfies z.ZodType<CardPointeTerminalSaleRequestBody>;

export const CardPointeTerminalSaleRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/api/v4/authCard'>;
    body: typeof CardPointeTerminalSaleRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/api/v4/authCard'),
    body: CardPointeTerminalSaleRequestBodySchema,
}) satisfies z.ZodType<CardPointeTerminalSaleRequestDetails>;

type CardPointeTerminalSaleApprovedAuthorizationBody =
    CardPointeTerminalSaleAuthorizationBody & {
        readonly respstat: 'A';
        readonly retref: string;
        readonly amount: string;
        readonly merchid: string;
    };

type CardPointeTerminalSaleDeclinedAuthorizationBody =
    CardPointeTerminalSaleAuthorizationBody & {
        readonly respstat: 'C';
        readonly resptext: string;
    };

export const CardPointeTerminalSaleAuthorizationBodySchema: z.ZodObject<{
    respstat: z.ZodUnion<readonly [z.ZodLiteral<'A'>, z.ZodLiteral<'C'>]>;
    amount: z.ZodOptional<z.ZodString>;
    resptext: z.ZodOptional<z.ZodString>;
    authcode: z.ZodOptional<z.ZodString>;
    respcode: z.ZodOptional<z.ZodString>;
    merchid: z.ZodOptional<z.ZodString>;
    cvvresp: z.ZodOptional<z.ZodString>;
    retref: z.ZodOptional<z.ZodString>;
    avsresp: z.ZodOptional<z.ZodString>;
    respproc: z.ZodOptional<z.ZodString>;
    batchid: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    account: z.ZodOptional<z.ZodString>;
    expiry: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    binInfo: z.ZodOptional<z.ZodUnknown>;
    emvTagData: z.ZodOptional<z.ZodUnknown>;
    tokenExpired: z.ZodOptional<z.ZodBoolean>;
    entrymode: z.ZodOptional<z.ZodString>;
    bintype: z.ZodOptional<z.ZodString>;
    orderId: z.ZodOptional<z.ZodString>;
    receiptData: z.ZodOptional<z.ZodUnknown>;
}> = z.object({
    respstat: z.union([z.literal('A'), z.literal('C')]),
    amount: z.string().min(1).optional(),
    resptext: z.string().min(1).optional(),
    authcode: z.string().min(1).optional(),
    respcode: z.string().min(1).optional(),
    merchid: z.string().min(1).optional(),
    cvvresp: z.string().min(1).optional(),
    retref: z.string().min(1).optional(),
    avsresp: z.string().min(1).optional(),
    respproc: z.string().min(1).optional(),
    batchid: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    account: z.string().min(1).optional(),
    expiry: z.string().min(1).optional(),
    name: z.string().optional(),
    binInfo: z.unknown().optional(),
    emvTagData: z.unknown().optional(),
    tokenExpired: z.boolean().optional(),
    entrymode: z.string().min(1).optional(),
    bintype: z.string().optional(),
    orderId: z.string().min(1).optional(),
    receiptData: z.unknown().optional(),
}) satisfies z.ZodType<CardPointeTerminalSaleAuthorizationBody>;

export const CardPointeTerminalSaleErrorBodySchema: z.ZodObject<{
    errorCode: z.ZodString;
    errorMessage: z.ZodString;
    authResponse: z.ZodOptional<
        typeof CardPointeTerminalSaleAuthorizationBodySchema
    >;
}> = z.object({
    errorCode: z.string().min(1),
    errorMessage: z.string().min(1),
    authResponse: CardPointeTerminalSaleAuthorizationBodySchema.optional(),
}) satisfies z.ZodType<CardPointeTerminalSaleErrorBody>;

export const CardPointeTerminalSaleApprovedAuthorizationBodySchema: z.ZodType<CardPointeTerminalSaleApprovedAuthorizationBody> =
    CardPointeTerminalSaleAuthorizationBodySchema.extend({
        respstat: z.literal('A'),
        retref: z.string().min(1),
        amount: z.string().min(1),
        merchid: z.string().min(1),
    });

export const CardPointeTerminalSaleDeclinedAuthorizationBodySchema: z.ZodType<CardPointeTerminalSaleDeclinedAuthorizationBody> =
    CardPointeTerminalSaleAuthorizationBodySchema.extend({
        respstat: z.literal('C'),
        resptext: z.string().min(1),
    });

export const CardPointeTerminalSaleApprovedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalSaleOutcome.APPROVED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeTerminalSaleApprovedAuthorizationBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.APPROVED),
    httpStatus: z.literal(200),
    body: CardPointeTerminalSaleApprovedAuthorizationBodySchema,
});

export const CardPointeTerminalSaleDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalSaleOutcome.DECLINED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeTerminalSaleDeclinedAuthorizationBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.DECLINED),
    httpStatus: z.literal(200),
    body: CardPointeTerminalSaleDeclinedAuthorizationBodySchema,
});

export const CardPointeTerminalSaleCanceledResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalSaleOutcome.CANCELED>;
    httpStatus: z.ZodNumber;
    body: typeof CardPointeTerminalSaleErrorBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.CANCELED),
    httpStatus: z.number().int().positive(),
    body: CardPointeTerminalSaleErrorBodySchema,
});

export const CardPointeTerminalSaleMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CardPointeTerminalSaleOutcome.MALFORMED_RESPONSE
    >;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CardPointeTerminalSaleResponseDetailsSchema: z.ZodType<CardPointeTerminalSaleResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeTerminalSaleApprovedResponseDetailsSchema,
        CardPointeTerminalSaleDeclinedResponseDetailsSchema,
        CardPointeTerminalSaleCanceledResponseDetailsSchema,
        CardPointeTerminalSaleMalformedResponseDetailsSchema,
    ]);

export const CardPointeTerminalSaleRequestLifecyclePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeTerminalSaleLifecyclePayloadType.REQUEST
    >;
    request_details: typeof CardPointeTerminalSaleRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        CardPointeTerminalSaleLifecyclePayloadType.REQUEST
    ),
    request_details: CardPointeTerminalSaleRequestDetailsSchema,
}) satisfies z.ZodType<CardPointeTerminalSaleRequestLifecyclePayload>;

export const CardPointeTerminalSaleResponseLifecyclePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeTerminalSaleLifecyclePayloadType.RESPONSE
    >;
    response_details: typeof CardPointeTerminalSaleResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        CardPointeTerminalSaleLifecyclePayloadType.RESPONSE
    ),
    response_details: CardPointeTerminalSaleResponseDetailsSchema,
}) satisfies z.ZodType<CardPointeTerminalSaleResponseLifecyclePayload>;

export const CardPointeTerminalSaleLifecyclePayloadSchema: z.ZodType<CardPointeTerminalSaleLifecyclePayload> =
    z.discriminatedUnion('gateway_lifecycle_payload_type', [
        CardPointeTerminalSaleRequestLifecyclePayloadSchema,
        CardPointeTerminalSaleResponseLifecyclePayloadSchema,
    ]);

export const CardPointeTerminalTokenizationAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeTerminalTokenizationRequestBodySchema: z.ZodObject<{
    merchantId: z.ZodString;
    hsn: z.ZodString;
    amount: z.ZodString;
    includeSignature: z.ZodOptional<z.ZodBoolean>;
    beep: z.ZodOptional<z.ZodBoolean>;
}> = z.object({
    merchantId: z.string().min(1),
    hsn: z.string().min(1),
    amount: CardPointeTerminalTokenizationAmountSchema,
    includeSignature: z.boolean().optional(),
    beep: z.boolean().optional(),
}) satisfies z.ZodType<CardPointeTerminalTokenizationRequestBody>;

export const CardPointeTerminalTokenizationRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/api/v4/readCard'>;
    body: typeof CardPointeTerminalTokenizationRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/api/v4/readCard'),
    body: CardPointeTerminalTokenizationRequestBodySchema,
}) satisfies z.ZodType<CardPointeTerminalTokenizationRequestDetails>;

export const CloverTerminalSaleAmountSchema: z.ZodNumber = z
    .number()
    .int()
    .min(1)
    .max(999999999);

export const CloverTerminalSaleRequestHeadersSchema: z.ZodObject<{
    deviceId: z.ZodString;
    idempotencyKey: z.ZodString;
    posId: z.ZodString;
    userAgent: z.ZodString;
    timeoutSeconds: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    deviceId: z.string().min(1),
    idempotencyKey: z.string().min(1),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
}) satisfies z.ZodType<CloverTerminalSaleRequestHeaders>;

export const CloverTerminalSaleRequestBodySchema: z.ZodObject<{
    amount: z.ZodNumber;
    externalPaymentId: z.ZodString;
    final: z.ZodLiteral<true>;
    capture: z.ZodOptional<z.ZodLiteral<true>>;
    externalReferenceId: z.ZodOptional<z.ZodString>;
    tipAmount: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    amount: CloverTerminalSaleAmountSchema,
    externalPaymentId: z.string().min(1),
    final: z.literal(true),
    capture: z.literal(true).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
    tipAmount: z.number().int().min(0).max(999999999).optional(),
}) satisfies z.ZodType<CloverTerminalSaleRequestBody>;

export const CloverTerminalSaleRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/payments'>;
    headers: typeof CloverTerminalSaleRequestHeadersSchema;
    body: typeof CloverTerminalSaleRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/payments'),
    headers: CloverTerminalSaleRequestHeadersSchema,
    body: CloverTerminalSaleRequestBodySchema,
}) satisfies z.ZodType<CloverTerminalSaleRequestDetails>;

type CloverTerminalSaleApprovedPaymentBody =
    CloverTerminalSalePaymentBody & {
        readonly id: string;
        readonly amount: number;
        readonly result: typeof CloverPaymentResult.SUCCESS;
    };

type CloverTerminalSaleDeclinedPaymentBody = CloverTerminalSalePaymentBody & {
    readonly result: typeof CloverPaymentResult.FAIL;
};

export const CloverTerminalSaleCardTransactionBodyFieldsSchema: z.ZodObject<{
    authCode: z.ZodOptional<z.ZodString>;
    referenceId: z.ZodOptional<z.ZodString>;
    state: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    transactionNo: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<z.ZodString>;
}> = z.object({
    authCode: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverTerminalSaleCardTransactionBody>;

export const CloverTerminalSaleCardTransactionBodySchema: z.ZodType<CloverTerminalSaleCardTransactionBody> =
    z.preprocess(
        omitEmptyCloverCardTransactionText,
        CloverTerminalSaleCardTransactionBodyFieldsSchema
    );

export const CloverPaymentResultSchema: z.ZodType<CloverPaymentResult> = z.enum(
    [
        CloverPaymentResult.SUCCESS,
        CloverPaymentResult.FAIL,
        CloverPaymentResult.INITIATED,
        CloverPaymentResult.VOIDED,
        CloverPaymentResult.VOIDING,
        CloverPaymentResult.VOID_FAILED,
        CloverPaymentResult.AUTH,
        CloverPaymentResult.AUTH_COMPLETED,
        CloverPaymentResult.DISCOUNT,
    ]
);

export const CloverTerminalSalePaymentBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<z.ZodNumber>;
    result: typeof CloverPaymentResultSchema;
    cardTransaction: z.ZodOptional<
        typeof CloverTerminalSaleCardTransactionBodySchema
    >;
    externalPaymentId: z.ZodOptional<z.ZodString>;
    externalReferenceId: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).optional(),
    amount: z.number().int().min(1).max(999999999).optional(),
    result: CloverPaymentResultSchema,
    cardTransaction: CloverTerminalSaleCardTransactionBodySchema.optional(),
    externalPaymentId: z.string().min(1).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
}) satisfies z.ZodType<CloverTerminalSalePaymentBody>;

export const CloverTerminalSaleApprovedPaymentBodySchema: z.ZodType<CloverTerminalSaleApprovedPaymentBody> =
    CloverTerminalSalePaymentBodySchema.extend({
        id: z.string().min(1),
        amount: z.number().int().min(1).max(999999999),
        result: z.literal(CloverPaymentResult.SUCCESS),
    });

export const CloverTerminalSaleDeclinedPaymentBodySchema: z.ZodType<CloverTerminalSaleDeclinedPaymentBody> =
    CloverTerminalSalePaymentBodySchema.extend({
        result: z.literal(CloverPaymentResult.FAIL),
    });

export const CloverTerminalSaleErrorBodySchema: z.ZodObject<{
    message: z.ZodString;
    type: z.ZodString;
    code: z.ZodOptional<z.ZodString>;
    requestId: z.ZodOptional<z.ZodString>;
    requestType: z.ZodOptional<z.ZodString>;
}> = z.object({
    message: z.string().min(1),
    type: z.string().min(1),
    code: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    requestType: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverTerminalSaleErrorBody>;

export const CloverTerminalSaleApprovedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.APPROVED>;
    httpStatus: z.ZodLiteral<200>;
    body: z.ZodObject<{
        payment: typeof CloverTerminalSaleApprovedPaymentBodySchema;
    }>;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.APPROVED),
    httpStatus: z.literal(200),
    body: z.object({
        payment: CloverTerminalSaleApprovedPaymentBodySchema,
    }),
});

export const CloverTerminalSaleDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.DECLINED>;
    httpStatus: z.ZodLiteral<200>;
    body: z.ZodObject<{
        payment: typeof CloverTerminalSaleDeclinedPaymentBodySchema;
    }>;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.DECLINED),
    httpStatus: z.literal(200),
    body: z.object({
        payment: CloverTerminalSaleDeclinedPaymentBodySchema,
    }),
});

export const CloverTerminalSaleCanceledResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.CANCELED>;
    httpStatus: z.ZodLiteral<209>;
    body: typeof CloverTerminalSaleErrorBodySchema;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.CANCELED),
    httpStatus: z.literal(209),
    body: CloverTerminalSaleErrorBodySchema,
});

export const CloverTerminalSaleMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CloverTerminalSaleResponseDetailsSchema: z.ZodType<CloverTerminalSaleResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverTerminalSaleApprovedResponseDetailsSchema,
        CloverTerminalSaleDeclinedResponseDetailsSchema,
        CloverTerminalSaleCanceledResponseDetailsSchema,
        CloverTerminalSaleMalformedResponseDetailsSchema,
    ]);

export const CloverTerminalSaleRequestLifecyclePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CloverTerminalSaleLifecyclePayloadType.REQUEST
    >;
    request_details: typeof CloverTerminalSaleRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        CloverTerminalSaleLifecyclePayloadType.REQUEST
    ),
    request_details: CloverTerminalSaleRequestDetailsSchema,
}) satisfies z.ZodType<CloverTerminalSaleRequestLifecyclePayload>;

export const CloverTerminalSaleResponseLifecyclePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CloverTerminalSaleLifecyclePayloadType.RESPONSE
    >;
    response_details: typeof CloverTerminalSaleResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        CloverTerminalSaleLifecyclePayloadType.RESPONSE
    ),
    response_details: CloverTerminalSaleResponseDetailsSchema,
}) satisfies z.ZodType<CloverTerminalSaleResponseLifecyclePayload>;

export const CloverTerminalSaleLifecyclePayloadSchema: z.ZodType<CloverTerminalSaleLifecyclePayload> =
    z.discriminatedUnion('gateway_lifecycle_payload_type', [
        CloverTerminalSaleRequestLifecyclePayloadSchema,
        CloverTerminalSaleResponseLifecyclePayloadSchema,
    ]);

export const CardPointeCnpTokenSaleAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeFundingRequestQuerySchema: z.ZodType<CardPointeFundingRequestQuery> =
    z.object({
        merchid: z.string().min(1),
        date: z.string().regex(/^(?:\d{4}|\d{8})$/),
        page: z.string().regex(/^\d+$/).optional(),
        limit: z.string().regex(/^\d+$/).optional(),
    });

export const CardPointeFundingRequestDetailsSchema: z.ZodType<CardPointeFundingRequestDetails> =
    z.object({
        method: z.literal('GET'),
        path: z.literal('/cardconnect/rest/funding'),
        query: CardPointeFundingRequestQuerySchema,
    });

export const CardPointeReconciliationLookupRequestSchema: z.ZodType<CardPointeReconciliationLookupRequest> =
    z.object({
        funding_date: z.string().regex(/^(?:\d{4}|\d{8})$/),
        gateway_txn_id: z.string().min(1),
        page: z.number().int().positive().optional(),
        limit: z.number().int().positive().optional(),
    });

export const CardPointeReconciliationLookupRequestDetailsSchema: z.ZodType<CardPointeReconciliationLookupRequestDetails> =
    z.object({
        fundingRequestDetails: CardPointeFundingRequestDetailsSchema,
        lookup: CardPointeReconciliationLookupRequestSchema,
    });

export const CloverReconciliationLookupRequestSchema: z.ZodType<CloverReconciliationLookupRequest> =
    z.object({
        merchant_transaction_id: z.string().min(1),
        settlement_date: z.string().min(1).optional(),
    });

export const CloverReconciliationLookupRequestDetailsSchema: z.ZodType<CloverReconciliationLookupRequestDetails> =
    z.object({
        statusRequestDetails: CloverTransactionStatusRequestDetailsSchema,
        lookup: CloverReconciliationLookupRequestSchema,
    });

export const CardPointeRefundAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeRefundRequestBodySchema: z.ZodObject<{
    merchid: z.ZodString;
    retref: z.ZodString;
    amount: z.ZodOptional<z.ZodString>;
    orderid: z.ZodOptional<z.ZodString>;
}> = z.object({
    merchid: z.string().min(1),
    retref: z.string().min(1),
    amount: CardPointeRefundAmountSchema.optional(),
    orderid: z.string().min(1).optional(),
}) satisfies z.ZodType<CardPointeRefundRequestBody>;

export const CardPointeRefundRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/cardconnect/rest/refund'>;
    body: typeof CardPointeRefundRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/cardconnect/rest/refund'),
    body: CardPointeRefundRequestBodySchema,
}) satisfies z.ZodType<CardPointeRefundRequestDetails>;

export const CardPointeVoidAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeVoidRequestBodySchema: z.ZodObject<{
    merchid: z.ZodString;
    retref: z.ZodString;
    amount: z.ZodOptional<z.ZodString>;
}> = z.object({
    merchid: z.string().min(1),
    retref: z.string().min(1),
    amount: CardPointeVoidAmountSchema.optional(),
}) satisfies z.ZodType<CardPointeVoidRequestBody>;

export const CardPointeVoidRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/cardconnect/rest/void'>;
    body: typeof CardPointeVoidRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/cardconnect/rest/void'),
    body: CardPointeVoidRequestBodySchema,
}) satisfies z.ZodType<CardPointeVoidRequestDetails>;

export const CloverRefundAmountSchema: z.ZodNumber = z
    .number()
    .int()
    .min(1)
    .max(999999999);

export const CloverCloudPayRefundRequestHeadersSchema: z.ZodObject<{
    deviceId: z.ZodString;
    idempotencyKey: z.ZodString;
    posId: z.ZodString;
    userAgent: z.ZodString;
    timeoutSeconds: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    deviceId: z.string().min(1),
    idempotencyKey: z.string().min(1),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
}) satisfies z.ZodType<CloverCloudPayRefundRequestHeaders>;

export const CloverCloudPayRefundRequestBodySchema: z.ZodObject<{
    amount: z.ZodOptional<typeof CloverRefundAmountSchema>;
    fullRefund: z.ZodOptional<z.ZodBoolean>;
}> = z.object({
    amount: CloverRefundAmountSchema.optional(),
    fullRefund: z.boolean().optional(),
}) satisfies z.ZodType<CloverCloudPayRefundRequestBody>;

export const CloverCloudPayRefundRequestDetailsSchema: z.ZodObject<{
    kind: z.ZodLiteral<typeof CloverRefundKind.CLOUD_PAY>;
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/payments/{paymentId}/refunds'>;
    paymentId: z.ZodString;
    headers: typeof CloverCloudPayRefundRequestHeadersSchema;
    body: typeof CloverCloudPayRefundRequestBodySchema;
}> = z.object({
    kind: z.literal(CloverRefundKind.CLOUD_PAY),
    method: z.literal('POST'),
    path: z.literal('/v1/payments/{paymentId}/refunds'),
    paymentId: z.string().min(1),
    headers: CloverCloudPayRefundRequestHeadersSchema,
    body: CloverCloudPayRefundRequestBodySchema,
}) satisfies z.ZodType<CloverCloudPayRefundRequestDetails>;

export const CloverEcommerceRefundRequestDetailsSchema = z
    .object({
        kind: z.literal(CloverRefundKind.ECOMMERCE),
        method: z.literal('POST'),
        path: z.literal('/v1/refunds'),
        chargeId: z.string().min(1),
        headers: z
            .object({
                idempotencyKey: z.string().min(1),
                userAgent: z.string().min(1),
            })
            .strict(),
        body: z
            .object({
                charge: z.string().min(1),
                amount: CloverRefundAmountSchema.optional(),
            })
            .strict(),
    })
    .strict() satisfies z.ZodType<CloverEcommerceRefundRequestDetails>;

export const CloverRefundRequestDetailsSchema: z.ZodType<CloverRefundRequestDetails> =
    z.discriminatedUnion('kind', [
        CloverCloudPayRefundRequestDetailsSchema,
        CloverEcommerceRefundRequestDetailsSchema,
    ]);

export const CloverVoidReasonSchema: z.ZodType<CloverVoidReason> =
    z.enum(CloverVoidReason);

export const CloverVoidRequestHeadersSchema: z.ZodObject<{
    deviceId: z.ZodString;
    idempotencyKey: z.ZodString;
    posId: z.ZodString;
    userAgent: z.ZodString;
    timeoutSeconds: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    deviceId: z.string().min(1),
    idempotencyKey: z.string().min(1),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
}) satisfies z.ZodType<CloverVoidRequestHeaders>;

export const CloverVoidRequestBodySchema: z.ZodObject<{
    voidReason: typeof CloverVoidReasonSchema;
}> = z.object({
    voidReason: CloverVoidReasonSchema,
}) satisfies z.ZodType<CloverVoidRequestBody>;

export const CloverCloudPayVoidRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/payments/{paymentId}/void'>;
    paymentId: z.ZodString;
    headers: typeof CloverVoidRequestHeadersSchema;
    body: typeof CloverVoidRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/payments/{paymentId}/void'),
    paymentId: z.string().min(13).max(13),
    headers: CloverVoidRequestHeadersSchema,
    body: CloverVoidRequestBodySchema,
}) satisfies z.ZodType<CloverCloudPayVoidRequestDetails>;

export const CloverEcommerceVoidRequestDetailsSchema = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/refunds'),
    chargeId: z.string().min(1),
    headers: z.object({ userAgent: z.string().min(1) }),
    body: z.object({ charge: z.string().min(1) }),
}) satisfies z.ZodType<CloverEcommerceVoidRequestDetails>;

export const CloverVoidRequestDetailsSchema = z.discriminatedUnion('path', [
    CloverCloudPayVoidRequestDetailsSchema,
    CloverEcommerceVoidRequestDetailsSchema,
]) satisfies z.ZodType<CloverVoidRequestDetails>;

export const CardPointeCnpTokenSaleRequestBodySchema: z.ZodObject<{
    merchid: z.ZodString;
    account: z.ZodString;
    amount: z.ZodString;
    currency: z.ZodString;
    capture: z.ZodLiteral<'y'>;
    orderid: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    expiry: z.ZodOptional<z.ZodString>;
    address: z.ZodOptional<z.ZodString>;
    city: z.ZodOptional<z.ZodString>;
    region: z.ZodOptional<z.ZodString>;
    country: z.ZodOptional<z.ZodString>;
    postal: z.ZodOptional<z.ZodString>;
}> = z.object({
    merchid: z.string().min(1),
    account: z.string().min(1),
    amount: CardPointeCnpTokenSaleAmountSchema,
    currency: z.string().length(3),
    capture: z.literal('y'),
    orderid: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
    expiry: z.string().regex(/^\d{4}$/).optional(),
    address: z.string().min(1).optional(),
    city: z.string().min(1).optional(),
    region: z.string().min(1).optional(),
    country: z.string().min(1).optional(),
    postal: z.string().min(1).optional(),
}) satisfies z.ZodType<CardPointeCnpTokenSaleRequestBody>;

export const CardPointeCnpTokenSaleRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'PUT'>;
    path: z.ZodLiteral<'/cardconnect/rest/auth'>;
    body: typeof CardPointeCnpTokenSaleRequestBodySchema;
}> = z.object({
    method: z.literal('PUT'),
    path: z.literal('/cardconnect/rest/auth'),
    body: CardPointeCnpTokenSaleRequestBodySchema,
}) satisfies z.ZodType<CardPointeCnpTokenSaleRequestDetails>;

const CloverMinorUnitMaximum = 999999999;

export const CloverCnpTokenSaleAmountSchema: z.ZodNumber = z
    .number()
    .int()
    .min(1)
    .max(CloverMinorUnitMaximum);

export const CloverCnpTokenSaleCurrencySchema: z.ZodString = z
    .string()
    .regex(/^[a-z]{3}$/);

export const CloverCnpTokenSaleRequestHeadersSchema: z.ZodObject<{
    userAgent: z.ZodString;
    forwardedFor: z.ZodString;
}> = z.object({
    userAgent: z.string().min(1),
    forwardedFor: z.string().min(1),
}) satisfies z.ZodType<CloverCnpTokenSaleRequestHeaders>;

export const CloverCnpTokenSaleFirstStoredCredentialsSchema: z.ZodObject<{
    sequence: z.ZodLiteral<'FIRST'>;
    is_scheduled: z.ZodLiteral<false>;
    initiator: z.ZodLiteral<'CARDHOLDER'>;
}> =
    z
        .object({
            sequence: z.literal('FIRST'),
            is_scheduled: z.literal(false),
            initiator: z.literal('CARDHOLDER'),
        })
        .strict() satisfies z.ZodType<CloverCnpTokenSaleFirstStoredCredentials>;

export const CloverCnpTokenSaleSubsequentStoredCredentialsSchema: z.ZodObject<{
    sequence: z.ZodLiteral<'SUBSEQUENT'>;
    is_scheduled: z.ZodOptional<z.ZodBoolean>;
    initiator: z.ZodOptional<
        z.ZodUnion<
            readonly [z.ZodLiteral<'MERCHANT'>, z.ZodLiteral<'CARDHOLDER'>]
        >
    >;
}> =
    z
        .object({
            sequence: z.literal('SUBSEQUENT'),
            is_scheduled: z.boolean().optional(),
            initiator: z
                .union([z.literal('MERCHANT'), z.literal('CARDHOLDER')])
                .optional(),
        })
        .strict() satisfies z.ZodType<CloverCnpTokenSaleSubsequentStoredCredentials>;

export const CloverCnpTokenSaleStoredCredentialsSchema: z.ZodType<CloverCnpTokenSaleStoredCredentials> =
    z.discriminatedUnion('sequence', [
        CloverCnpTokenSaleFirstStoredCredentialsSchema,
        CloverCnpTokenSaleSubsequentStoredCredentialsSchema,
    ]);

export const CloverCnpTokenSaleRequestBodySchema: z.ZodObject<{
    amount: typeof CloverCnpTokenSaleAmountSchema;
    currency: typeof CloverCnpTokenSaleCurrencySchema;
    source: z.ZodString;
    intent: z.ZodOptional<z.ZodLiteral<'save_credential_on_file'>>;
    capture: z.ZodOptional<z.ZodBoolean>;
    description: z.ZodOptional<z.ZodString>;
    external_reference_id: z.ZodOptional<z.ZodString>;
    receipt_email: z.ZodOptional<z.ZodString>;
    stored_credentials: z.ZodOptional<
        typeof CloverCnpTokenSaleStoredCredentialsSchema
    >;
}> = z.object({
    amount: CloverCnpTokenSaleAmountSchema,
    currency: CloverCnpTokenSaleCurrencySchema,
    source: z.string().min(1),
    intent: z.literal('save_credential_on_file').optional(),
    capture: z.boolean().optional(),
    description: z.string().min(1).max(255).optional(),
    external_reference_id: z.string().min(1).max(12).optional(),
    receipt_email: z.string().min(1).optional(),
    stored_credentials: CloverCnpTokenSaleStoredCredentialsSchema.optional(),
}) satisfies z.ZodType<CloverCnpTokenSaleRequestBody>;

export const CloverCnpTokenSaleRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/charges'>;
    headers: typeof CloverCnpTokenSaleRequestHeadersSchema;
    body: typeof CloverCnpTokenSaleRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/charges'),
    headers: CloverCnpTokenSaleRequestHeadersSchema,
    body: CloverCnpTokenSaleRequestBodySchema,
}) satisfies z.ZodType<CloverCnpTokenSaleRequestDetails>;

const CloverTimeoutMaximum = 300;
const LifecycleNumericMagnitudeMaximum = Number.MAX_SAFE_INTEGER;

export const PaymentGatewayTerminalSaleApprovedResultSchema: z.ZodObject<{
    status: z.ZodLiteral<typeof PaymentGatewayTerminalSaleStatus.APPROVED>;
    gatewayTransactionId: z.ZodString;
    approvedAmount: z.ZodString;
    authorizationCode: z.ZodOptional<z.ZodString>;
    batchId: z.ZodOptional<z.ZodString>;
    maskedAccount: z.ZodOptional<z.ZodString>;
    gatewayToken: z.ZodOptional<z.ZodString>;
}> = z.object({
    status: z.literal(PaymentGatewayTerminalSaleStatus.APPROVED),
    gatewayTransactionId: z.string().min(1),
    approvedAmount: z.string().min(1),
    authorizationCode: z.string().min(1).optional(),
    batchId: z.string().min(1).optional(),
    maskedAccount: z.string().min(1).optional(),
    gatewayToken: z.string().min(1).optional(),
});

export const PaymentGatewayTerminalSaleDeclinedResultSchema: z.ZodObject<{
    status: z.ZodLiteral<typeof PaymentGatewayTerminalSaleStatus.DECLINED>;
    message: z.ZodString;
    gatewayTransactionId: z.ZodOptional<z.ZodString>;
    responseCode: z.ZodOptional<z.ZodString>;
}> = z.object({
    status: z.literal(PaymentGatewayTerminalSaleStatus.DECLINED),
    message: z.string().min(1),
    gatewayTransactionId: z.string().min(1).optional(),
    responseCode: z.string().min(1).optional(),
});

export const PaymentGatewayTerminalSaleCanceledResultSchema: z.ZodObject<{
    status: z.ZodLiteral<typeof PaymentGatewayTerminalSaleStatus.CANCELED>;
    message: z.ZodString;
    gatewayTransactionId: z.ZodOptional<z.ZodString>;
}> = z.object({
    status: z.literal(PaymentGatewayTerminalSaleStatus.CANCELED),
    message: z.string().min(1),
    gatewayTransactionId: z.string().min(1).optional(),
});

export const PaymentGatewayTerminalSaleMalformedResultSchema: z.ZodObject<{
    status: z.ZodLiteral<
        typeof PaymentGatewayTerminalSaleStatus.MALFORMED_RESPONSE
    >;
    message: z.ZodString;
}> = z.object({
    status: z.literal(PaymentGatewayTerminalSaleStatus.MALFORMED_RESPONSE),
    message: z.string().min(1),
});

export const PaymentGatewayTerminalSaleResultSchema: z.ZodType<PaymentGatewayTerminalSaleResult> =
    z.discriminatedUnion('status', [
        PaymentGatewayTerminalSaleApprovedResultSchema,
        PaymentGatewayTerminalSaleDeclinedResultSchema,
        PaymentGatewayTerminalSaleCanceledResultSchema,
        PaymentGatewayTerminalSaleMalformedResultSchema,
    ]);

export const CardPointeLifecyclePreflightRequestDetailsSchema: z.ZodType<CardPointeLifecyclePreflightRequestDetails> =
    z.object({
        gateway_operation: z.literal(
            CardPointeLifecyclePreflightGatewayOperation.TERMINAL_SALE
        ),
        terminal_sale_request_details:
            CardPointeTerminalSaleRequestDetailsSchema,
    });

export const CardPointeLifecyclePreflightRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT
    >;
    request_details: typeof CardPointeLifecyclePreflightRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT
    ),
    request_details: CardPointeLifecyclePreflightRequestDetailsSchema,
}) satisfies z.ZodType<CardPointeLifecyclePreflightRequestPayload>;

export const CardPointeLifecycleFinalizationRequestDetailsSchema: z.ZodType<CardPointeLifecycleFinalizationRequestDetails> =
    z.object({
        gateway_operation: z.literal(
            CardPointeLifecycleFinalizationGatewayOperation.TERMINAL_SALE
        ),
        terminal_sale_response_details:
            CardPointeTerminalSaleResponseDetailsSchema,
        terminal_sale_result: z.lazy(
            () => PaymentGatewayTerminalSaleResultSchema
        ),
    });

export const CardPointeLifecycleFinalizationRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION
    >;
    request_details: typeof CardPointeLifecycleFinalizationRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION
    ),
    request_details: CardPointeLifecycleFinalizationRequestDetailsSchema,
}) satisfies z.ZodType<CardPointeLifecycleFinalizationRequestPayload>;

export const CloverLifecyclePreflightRequestDetailsSchema: z.ZodType<CloverLifecyclePreflightRequestDetails> =
    z.object({
        gateway_operation: z.literal(
            CloverLifecyclePreflightGatewayOperation.TERMINAL_SALE
        ),
        terminal_sale_request_details: CloverTerminalSaleRequestDetailsSchema,
    });

export const CloverLifecyclePreflightRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT
    >;
    request_details: typeof CloverLifecyclePreflightRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT
    ),
    request_details: CloverLifecyclePreflightRequestDetailsSchema,
}) satisfies z.ZodType<CloverLifecyclePreflightRequestPayload>;

export const CloverLifecycleFinalizationRequestDetailsSchema: z.ZodType<CloverLifecycleFinalizationRequestDetails> =
    z.object({
        gateway_operation: z.literal(
            CloverLifecycleFinalizationGatewayOperation.TERMINAL_SALE
        ),
        terminal_sale_response_details: CloverTerminalSaleResponseDetailsSchema,
        terminal_sale_result: z.lazy(
            () => PaymentGatewayTerminalSaleResultSchema
        ),
    });

export const CloverLifecycleFinalizationRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION
    >;
    request_details: typeof CloverLifecycleFinalizationRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION
    ),
    request_details: CloverLifecycleFinalizationRequestDetailsSchema,
}) satisfies z.ZodType<CloverLifecycleFinalizationRequestPayload>;

function normalizeStoredValorCardTokenSaleRequest(
    value: unknown
): unknown {
    const normalizedFlag = normalizeStoredJsonSafeIntegerFieldsAtPath(
        value,
        {
            domain: 'gateway-lifecycle-request.valor-card-sale.flag',
            path: [],
            fields: ['surchargeIndicator'],
            minimum: 0,
            maximum: 1,
        }
    );
    return normalizeStoredJsonNumberFieldsAtPath(normalizedFlag, {
        domain: 'gateway-lifecycle-request.valor-card-sale.amount',
        numericKind: 'bounded-binary64',
        path: [],
        fields: ['surchargeAmount'],
        minimum: 0,
        maximum: LifecycleNumericMagnitudeMaximum,
    });
}

function normalizeStoredValorVoidRequest(value: unknown): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: 'gateway-lifecycle-request.valor-void.flag',
        path: [],
        fields: ['surchargeindicator'],
        minimum: 1,
        maximum: 1,
    });
}

function normalizeStoredValorRefundRequest(value: unknown): unknown {
    const normalizedFlag = normalizeStoredJsonSafeIntegerFieldsAtPath(
        value,
        {
            domain: 'gateway-lifecycle-request.valor-refund.flag',
            path: [],
            fields: ['surchargeIndicator'],
            minimum: 0,
            maximum: 1,
        }
    );
    return normalizeStoredJsonNumberFieldsAtPath(normalizedFlag, {
        domain: 'gateway-lifecycle-request.valor-refund.amount',
        numericKind: 'bounded-binary64',
        path: [],
        fields: ['amount'],
        minimum: 0,
        maximum: LifecycleNumericMagnitudeMaximum,
    });
}

function normalizeStoredTerminalSaleDispatchRequest(
    value: unknown
): unknown {
    const normalizedSourceKey =
        normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
            domain: 'gateway-lifecycle-request.terminal-dispatch.source',
            path: ['source_document_key'],
            fields: [
                'transaction_details_type_id',
                'transaction_details_type_version',
            ],
            minimum: Number.MIN_SAFE_INTEGER,
            maximum: Number.MAX_SAFE_INTEGER,
        });
    const normalizedDetails = normalizeStoredJsonSafeIntegerFieldsAtPath(
        normalizedSourceKey,
        {
            domain: 'gateway-lifecycle-request.terminal-dispatch.details',
            path: ['transaction_details'],
            fields: [
                'ipw_system_id',
                'transaction_details_type_id',
                'transaction_details_type_version',
            ],
            minimum: Number.MIN_SAFE_INTEGER,
            maximum: Number.MAX_SAFE_INTEGER,
        }
    );
    return normalizeStoredJsonNumberFieldsAtPath(normalizedDetails, {
        domain: 'gateway-lifecycle-request.terminal-dispatch.amount',
        numericKind: 'bounded-binary64',
        path: ['transaction_details'],
        fields: ['amount'],
        minimum: -LifecycleNumericMagnitudeMaximum,
        maximum: LifecycleNumericMagnitudeMaximum,
    });
}

function normalizeStoredCloverTerminalSaleRequestDetails(
    value: unknown,
    domain: string
): unknown {
    const normalizedHeaders = normalizeStoredJsonSafeIntegerFieldsAtPath(
        value,
        {
            domain: `${domain}.headers`,
            path: ['headers'],
            fields: ['timeoutSeconds'],
            minimum: 0,
            maximum: CloverTimeoutMaximum,
        }
    );
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedHeaders, {
        domain: `${domain}.body`,
        path: ['body'],
        fields: ['amount', 'tipAmount'],
        minimum: 0,
        maximum: CloverMinorUnitMaximum,
    });
}

function normalizeStoredCloverRequestHeaders(
    value: unknown,
    path: readonly string[],
    domain: string
): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain,
        path,
        fields: ['timeoutSeconds'],
        minimum: 0,
        maximum: CloverTimeoutMaximum,
    });
}

function normalizeStoredCloverChargeRequest(
    value: unknown,
    domain: string
): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: `${domain}.body`,
        path: ['body'],
        fields: ['amount'],
        minimum: 1,
        maximum: CloverMinorUnitMaximum,
    });
}

function normalizeStoredGatewayRequestPayload(
    requestType: unknown,
    payload: unknown
): unknown {
    switch (requestType) {
        case GatewayLifecycleRequestType.BIN_LOOKUP:
            return normalizeStoredJsonSafeIntegerFieldsAtPath(payload, {
                domain: 'gateway-lifecycle-request.bin-lookup.version',
                path: [],
                fields: ['bin_lookup_evidence_version'],
                minimum: 1,
                maximum: 1,
            });
        case GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE:
            return normalizeStoredValorCardTokenSaleRequest(payload);
        case GatewayLifecycleRequestType.VALOR_VOID:
            return normalizeStoredValorVoidRequest(payload);
        case GatewayLifecycleRequestType.VALOR_REFUND:
            return normalizeStoredValorRefundRequest(payload);
        case GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH:
            return normalizeStoredTerminalSaleDispatchRequest(payload);
        case GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE:
        case GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE:
            return normalizeStoredCloverChargeRequest(
                payload,
                `gateway-lifecycle-request.${requestType}`
            );
        case GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE:
            return normalizeStoredCloverTerminalSalePayload(payload);
        case GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT:
            return normalizeStoredCloverPreflightPayload(payload);
        case GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION:
            return normalizeStoredCloverFinalizationPayload(payload);
        case GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION:
        case GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS:
        case GatewayLifecycleRequestType.CLOVER_VOID:
            return normalizeStoredCloverRequestHeaders(
                payload,
                ['headers'],
                `gateway-lifecycle-request.${requestType}.headers`
            );
        case GatewayLifecycleRequestType.CLOVER_REFUND:
            return normalizeStoredCloverRefundRequest(payload);
        case GatewayLifecycleRequestType.CLOVER_RECONCILIATION:
            return normalizeStoredCloverRequestHeaders(
                payload,
                ['statusRequestDetails', 'headers'],
                'gateway-lifecycle-request.clover-reconciliation.headers'
            );
        default:
            return payload;
    }
}

function normalizeStoredCloverTerminalSalePayload(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }
    return {
        ...rootResult.data,
        request_details: normalizeStoredCloverTerminalSaleRequestDetails(
            rootResult.data.request_details,
            'gateway-lifecycle-request.clover-terminal-sale.request-details'
        ),
    };
}

function normalizeStoredCloverPreflightPayload(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    const requestDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        rootResult.success ? rootResult.data.request_details : undefined
    );
    if (!rootResult.success || !requestDetailsResult.success) {
        return value;
    }
    return {
        ...rootResult.data,
        request_details: {
            ...requestDetailsResult.data,
            terminal_sale_request_details:
                normalizeStoredCloverTerminalSaleRequestDetails(
                    requestDetailsResult.data.terminal_sale_request_details,
                    'gateway-lifecycle-request.clover-preflight.terminal-sale'
                ),
        },
    };
}

function normalizeStoredCloverFinalizationPayload(value: unknown): unknown {
    const normalizedHttpStatus =
        normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
            domain:
                'gateway-lifecycle-request.clover-finalization.response',
            path: [
                'request_details',
                'terminal_sale_response_details',
            ],
            fields: ['httpStatus'],
            minimum: 1,
            maximum: Number.MAX_SAFE_INTEGER,
        });
    const rootResult =
        StoredJsonNumberRecordSchema.safeParse(normalizedHttpStatus);
    const requestDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        rootResult.success ? rootResult.data.request_details : undefined
    );
    const responseDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        requestDetailsResult.success
            ? requestDetailsResult.data.terminal_sale_response_details
            : undefined
    );
    if (
        !responseDetailsResult.success ||
        (responseDetailsResult.data.outcome !== 'approved' &&
            responseDetailsResult.data.outcome !== 'declined')
    ) {
        return normalizedHttpStatus;
    }
    return normalizeStoredJsonSafeIntegerFieldsAtPath(
        normalizedHttpStatus,
        {
            domain:
                'gateway-lifecycle-request.clover-finalization.payment',
            path: [
                'request_details',
                'terminal_sale_response_details',
                'body',
                'payment',
            ],
            fields: ['amount'],
            minimum: 1,
            maximum: CloverMinorUnitMaximum,
        }
    );
}

function normalizeStoredCloverRefundRequest(value: unknown): unknown {
    const normalizedHeaders = normalizeStoredCloverRequestHeaders(
        value,
        ['headers'],
        'gateway-lifecycle-request.clover-refund.headers'
    );
    const normalizedRequest = normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedHeaders, {
        domain: 'gateway-lifecycle-request.clover-refund.body',
        path: ['body'],
        fields: ['amount'],
        minimum: 1,
        maximum: CloverMinorUnitMaximum,
    });
    const requestResult = StoredJsonNumberRecordSchema.safeParse(normalizedRequest);
    return requestResult.success && requestResult.data.kind === undefined
        ? { ...requestResult.data, kind: CloverRefundKind.CLOUD_PAY }
        : normalizedRequest;
}

export function normalizeStoredGatewayLifecycleRequestNumericLeaves(
    value: unknown
): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }
    const normalizedPayload = normalizeStoredGatewayRequestPayload(
        rootResult.data.gateway_request_type,
        rootResult.data.gateway_request
    );
    if (normalizedPayload === rootResult.data.gateway_request) {
        return value;
    }
    return {
        ...rootResult.data,
        gateway_request: normalizedPayload,
    };
}

type GatewayLifecycleRequestVariantSchema<
    TRequestType extends GatewayLifecycleRequestType,
    TGatewayRequestSchema extends z.ZodType,
> = z.ZodObject<{
    gateway_request_type: z.ZodLiteral<TRequestType>;
    gateway_request: TGatewayRequestSchema;
}>;

function gatewayLifecycleRequestVariantSchema<
    TRequestType extends GatewayLifecycleRequestType,
    TGatewayRequestSchema extends z.ZodType,
>(
    gatewayRequestType: TRequestType,
    gatewayRequestSchema: TGatewayRequestSchema
): GatewayLifecycleRequestVariantSchema<TRequestType, TGatewayRequestSchema> {
    return z.object({
        gateway_request_type: z.literal(gatewayRequestType),
        gateway_request: gatewayRequestSchema,
    });
}

export const ValorAdditionalPromptSchema: z.ZodType<ValorAdditionalPrompt> =
    z.object({
        PROMPT_NAME: z.string().min(1),
        PROMPT_VALUE: z.string().min(1),
    });

export const ValorTerminalPublishPayloadV1Schema = z.object({
    TRAN_MODE: z.string().min(1),
    TRAN_CODE: z.string().min(1),
    AMOUNT: z.string().min(1),
    REQ_TXN_ID: z.string().min(1),
    INVOICENUMBER: z.string().min(1).optional(),
    VAS_LABEL1: z.string().min(1).optional(),
    VAS_DATA1: z.string().min(1).optional(),
    VAS_LABEL2: z.string().min(1).optional(),
    VAS_DATA2: z.string().min(1).optional(),
}) satisfies z.ZodType<ValorTerminalPublishPayloadV1>;

export const ValorTerminalPublishPayloadV2Schema: z.ZodType<ValorTerminalPublishPayloadV2> =
    ValorTerminalPublishPayloadV1Schema.extend({
        TIP_ENTRY: z.literal('1').optional(),
    });

export const ValorTerminalPublishGatewayRequestV1Schema: z.ZodType<ValorTerminalPublishGatewayRequestV1> =
    z.object({
        txn_type: z.literal('vc_publish'),
        channel_id: z.string(),
        version: z.string().min(1),
        payload: ValorTerminalPublishPayloadV1Schema,
    });

export const ValorTerminalPublishGatewayRequestV2Schema: z.ZodType<ValorTerminalPublishGatewayRequestV2> =
    z.object({
        txn_type: z.literal('vc_publish'),
        channel_id: z.string(),
        version: z.string().min(1),
        payload: ValorTerminalPublishPayloadV2Schema,
    });

export const ValorCardTokenSaleGatewayRequestSchema: z.ZodType<ValorCardTokenSaleGatewayRequest> =
    z.object({
        txn_type: z.literal('sale'),
        amount: z.string().min(1),
        surchargeIndicator: z.union([z.literal(0), z.literal(1)]),
        surchargeAmount: z.number().nonnegative(),
        token: z.string().min(1).optional(),
        invoicenumber: z.string().min(1),
        cardholdername: z.string().min(1),
        address: z.string().min(1).optional(),
        city: z.string().min(1).optional(),
        orderdescription: z.string().min(1).optional(),
        shipping_country: z.string().min(1).optional(),
        zip: z.string().min(1).optional(),
        additional_prompts: z.array(ValorAdditionalPromptSchema).optional(),
    });

export const ValorVoidGatewayRequestSchema: z.ZodType<ValorVoidGatewayRequest> =
    z.object({
        txn_type: z.literal('void'),
        ref_txn_id: z.string().min(1),
        surchargeindicator: z.literal(1),
        amount: z.string().min(1).optional(),
    });

export const ValorRefundPromptSchema: z.ZodType<ValorRefundPrompt> = z.object({
    name: z.string().min(1),
    value: z.string().min(1),
});

export const ValorRefundGatewayRequestSchema: z.ZodType<ValorRefundGatewayRequest> =
    z.object({
        txn_type: z.literal('refund'),
        amount: z.number().positive(),
        surchargeIndicator: z.union([z.literal(0), z.literal(1)]),
        ref_txn_id: z.string().min(1),
        sale_refund: z.literal('1'),
        merchantTransactionId: z.string().min(1),
        additionalPrompts: z.array(ValorRefundPromptSchema).optional(),
        rrn: z.string().min(1).optional(),
        auth_code: z.string().min(1).optional(),
        child_epi: z.string().min(1).optional(),
    });

export const ValorTransactionStatusGatewayRequestSchema: z.ZodType<ValorTransactionStatusGatewayRequest> =
    z.object({
        REQ_TXN_ID: z.string().min(1).optional(),
        TXN_ID: z.string().min(1).optional(),
        channel_id: z.string().min(1).optional(),
    });

export const ValorTransactionListGatewayRequestSchema: z.ZodType<ValorTransactionListGatewayRequest> =
    z.object({
        date_filter: z.string().min(1).optional(),
        amount: z.string().min(1).optional(),
        ADDNL_VAL_1: z.string().min(1).optional(),
    });

export const ValorReconciliationGatewayRequestSchema: z.ZodType<ValorReconciliationGatewayRequest> =
    z.object({
        gateway_txn_id: z.string().min(1),
        merchant_transaction_id: z.string().min(1).optional(),
        amount: z.string().min(1).optional(),
    });

export const AchPaymentGatewayRequestSchema: z.ZodType<AchPaymentGatewayRequest> =
    z.object({
        txn_type: z.literal('ach'),
        account_id: z.string().min(1),
    });

export const IntegrationReconciliationGatewayRequestSchema: z.ZodType<IntegrationReconciliationGatewayRequest> =
    z.object({
        source: z.string().min(1),
    });

export const TerminalSaleDispatchSourceDocumentKeySnapshotSchema:
    z.ZodType<TerminalSaleDispatchSourceDocumentKeySnapshot> =
    z.object({
        transaction_details_type_id: z.number().int(),
        transaction_details_type_version: z.number().int(),
    }).catchall(JsonValueSchema);

export const TerminalSaleDispatchTransactionDetailsSnapshotSchema:
    z.ZodType<TerminalSaleDispatchTransactionDetailsSnapshot> =
    z.object({
        amount: z.number(),
        currency: z.string().min(1),
        ipw_system_id: z.number().int(),
        transaction_details_type_id: z.number().int(),
        transaction_details_type_version: z.number().int(),
    }).catchall(JsonValueSchema);

export const TerminalSaleDispatchGatewayRequestSchema:
    z.ZodType<TerminalSaleDispatchGatewayRequest> =
    z.object({
        amount: z.string().min(1),
        merchant_transaction_id: z.string().min(1),
        source_document_key:
            TerminalSaleDispatchSourceDocumentKeySnapshotSchema,
        source_document_name: z.string().min(1),
        source_document_reference: z.string().min(1),
        transaction_details:
            TerminalSaleDispatchTransactionDetailsSnapshotSchema,
    });

export const ValorTerminalPublishGatewayLifecycleRequestV1Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V1,
    typeof ValorTerminalPublishGatewayRequestV1Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V1,
    ValorTerminalPublishGatewayRequestV1Schema
) satisfies z.ZodType<ValorTerminalPublishGatewayLifecycleRequestV1>;

export const ValorTerminalPublishGatewayLifecycleRequestV2Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V2,
    typeof ValorTerminalPublishGatewayRequestV2Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V2,
    ValorTerminalPublishGatewayRequestV2Schema
) satisfies z.ZodType<ValorTerminalPublishGatewayLifecycleRequestV2>;

export const ValorCardTokenSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE,
    typeof ValorCardTokenSaleGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE,
    ValorCardTokenSaleGatewayRequestSchema
) satisfies z.ZodType<ValorCardTokenSaleGatewayLifecycleRequest>;

export const ValorVoidGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_VOID,
    typeof ValorVoidGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_VOID,
    ValorVoidGatewayRequestSchema
) satisfies z.ZodType<ValorVoidGatewayLifecycleRequest>;

export const ValorRefundGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_REFUND,
    typeof ValorRefundGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_REFUND,
    ValorRefundGatewayRequestSchema
) satisfies z.ZodType<ValorRefundGatewayLifecycleRequest>;

export const ValorTransactionStatusGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS,
    typeof ValorTransactionStatusGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS,
    ValorTransactionStatusGatewayRequestSchema
) satisfies z.ZodType<ValorTransactionStatusGatewayLifecycleRequest>;

export const ValorTransactionListGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST,
    typeof ValorTransactionListGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST,
    ValorTransactionListGatewayRequestSchema
) satisfies z.ZodType<ValorTransactionListGatewayLifecycleRequest>;

export const ValorReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_RECONCILIATION,
    typeof ValorReconciliationGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_RECONCILIATION,
    ValorReconciliationGatewayRequestSchema
) satisfies z.ZodType<ValorReconciliationGatewayLifecycleRequest>;

export const CardPointeCnpTokenSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE,
    typeof CardPointeCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE,
    CardPointeCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CardPointeCnpTokenSaleGatewayLifecycleRequest>;

export const CardPointeTerminalSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE,
    typeof CardPointeTerminalSaleRequestLifecyclePayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE,
    CardPointeTerminalSaleRequestLifecyclePayloadSchema
) satisfies z.ZodType<CardPointeTerminalSaleGatewayLifecycleRequest>;

export const CardPointeLifecyclePreflightGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT,
    typeof CardPointeLifecyclePreflightRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT,
    CardPointeLifecyclePreflightRequestPayloadSchema
) satisfies z.ZodType<CardPointeLifecyclePreflightGatewayLifecycleRequest>;

export const CardPointeLifecycleFinalizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION,
    typeof CardPointeLifecycleFinalizationRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION,
    CardPointeLifecycleFinalizationRequestPayloadSchema
) satisfies z.ZodType<CardPointeLifecycleFinalizationGatewayLifecycleRequest>;

export const CardPointeTerminalTokenizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION,
    typeof CardPointeTerminalTokenizationRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION,
    CardPointeTerminalTokenizationRequestDetailsSchema
) satisfies z.ZodType<CardPointeTerminalTokenizationGatewayLifecycleRequest>;

export const CardPointeTransactionStatusGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS,
    typeof CardPointeTransactionStatusRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS,
    CardPointeTransactionStatusRequestDetailsSchema
) satisfies z.ZodType<CardPointeTransactionStatusGatewayLifecycleRequest>;

export const CardPointeVoidGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_VOID,
    typeof CardPointeVoidRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_VOID,
    CardPointeVoidRequestDetailsSchema
) satisfies z.ZodType<CardPointeVoidGatewayLifecycleRequest>;

export const CardPointeRefundGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_REFUND,
    typeof CardPointeRefundRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_REFUND,
    CardPointeRefundRequestDetailsSchema
) satisfies z.ZodType<CardPointeRefundGatewayLifecycleRequest>;

export const CardPointeReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_RECONCILIATION,
    typeof CardPointeReconciliationLookupRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_RECONCILIATION,
    CardPointeReconciliationLookupRequestDetailsSchema
) satisfies z.ZodType<CardPointeReconciliationGatewayLifecycleRequest>;

export const CardPointeStoredCardSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE,
    typeof CardPointeCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE,
    CardPointeCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CardPointeStoredCardSaleGatewayLifecycleRequest>;

export const CloverCnpTokenSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE,
    typeof CloverCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE,
    CloverCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CloverCnpTokenSaleGatewayLifecycleRequest>;

export const CloverTerminalSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE,
    typeof CloverTerminalSaleRequestLifecyclePayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE,
    CloverTerminalSaleRequestLifecyclePayloadSchema
) satisfies z.ZodType<CloverTerminalSaleGatewayLifecycleRequest>;

export const CloverLifecyclePreflightGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT,
    typeof CloverLifecyclePreflightRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT,
    CloverLifecyclePreflightRequestPayloadSchema
) satisfies z.ZodType<CloverLifecyclePreflightGatewayLifecycleRequest>;

export const CloverLifecycleFinalizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION,
    typeof CloverLifecycleFinalizationRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION,
    CloverLifecycleFinalizationRequestPayloadSchema
) satisfies z.ZodType<CloverLifecycleFinalizationGatewayLifecycleRequest>;

export const CloverTerminalTokenizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION,
    typeof CloverTerminalTokenizationRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION,
    CloverTerminalTokenizationRequestDetailsSchema
) satisfies z.ZodType<CloverTerminalTokenizationGatewayLifecycleRequest>;

export const CloverTransactionStatusGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS,
    typeof CloverTransactionStatusRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS,
    CloverTransactionStatusRequestDetailsSchema
) satisfies z.ZodType<CloverTransactionStatusGatewayLifecycleRequest>;

export const CloverVoidGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_VOID,
    typeof CloverVoidRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_VOID,
    CloverVoidRequestDetailsSchema
) satisfies z.ZodType<CloverVoidGatewayLifecycleRequest>;

export const CloverRefundGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_REFUND,
    typeof CloverRefundRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_REFUND,
    CloverRefundRequestDetailsSchema
) satisfies z.ZodType<CloverRefundGatewayLifecycleRequest>;

export const CloverReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_RECONCILIATION,
    typeof CloverReconciliationLookupRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_RECONCILIATION,
    CloverReconciliationLookupRequestDetailsSchema
) satisfies z.ZodType<CloverReconciliationGatewayLifecycleRequest>;

export const CloverStoredCardSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE,
    typeof CloverCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE,
    CloverCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CloverStoredCardSaleGatewayLifecycleRequest>;

export const AchPaymentGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.ACH_PAYMENT,
    typeof AchPaymentGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.ACH_PAYMENT,
    AchPaymentGatewayRequestSchema
) satisfies z.ZodType<AchPaymentGatewayLifecycleRequest>;

export const IntegrationReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.INTEGRATION_RECONCILIATION,
    typeof IntegrationReconciliationGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.INTEGRATION_RECONCILIATION,
    IntegrationReconciliationGatewayRequestSchema
) satisfies z.ZodType<IntegrationReconciliationGatewayLifecycleRequest>;

export const TerminalSaleDispatchGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH,
    typeof TerminalSaleDispatchGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH,
    TerminalSaleDispatchGatewayRequestSchema
) satisfies z.ZodType<TerminalSaleDispatchGatewayLifecycleRequest>;

export const BinLookupEvidenceV1Schema: z.ZodType<BinLookupEvidenceV1> =
    z.discriminatedUnion('bin_lookup_backend', [
        z.object({
            bin_lookup_evidence_version: z.literal(1),
            bin_lookup_backend: z.literal(BinLookupBackend.VALOR),
            pan_prefix: z.string().regex(/^\d{6,9}$/),
            card_type: z.enum(CardType),
            provider_response: JsonValueSchema,
        }),
        z.object({
            bin_lookup_evidence_version: z.literal(1),
            bin_lookup_backend: z.literal(BinLookupBackend.BINLOOKUPAPI),
            pan_prefix: z.string().regex(/^\d{6,9}$/),
            card_type: z.enum(CardType),
            provider_response: JsonValueSchema,
        }),
    ]);

export const BinLookupGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.BIN_LOOKUP,
    typeof BinLookupEvidenceV1Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.BIN_LOOKUP,
    BinLookupEvidenceV1Schema
) satisfies z.ZodType<BinLookupGatewayLifecycleRequest>;

export const GatewayLifecycleRequestSchema: z.ZodType<GatewayLifecycleRequest> =
    z.discriminatedUnion('gateway_request_type', [
        BinLookupGatewayLifecycleRequestSchema,
        AchPaymentGatewayLifecycleRequestSchema,
        CardPointeCnpTokenSaleGatewayLifecycleRequestSchema,
        CardPointeLifecycleFinalizationGatewayLifecycleRequestSchema,
        CardPointeLifecyclePreflightGatewayLifecycleRequestSchema,
        CardPointeReconciliationGatewayLifecycleRequestSchema,
        CardPointeRefundGatewayLifecycleRequestSchema,
        CardPointeStoredCardSaleGatewayLifecycleRequestSchema,
        CardPointeTerminalSaleGatewayLifecycleRequestSchema,
        CardPointeTerminalTokenizationGatewayLifecycleRequestSchema,
        CardPointeTransactionStatusGatewayLifecycleRequestSchema,
        CardPointeVoidGatewayLifecycleRequestSchema,
        CloverCnpTokenSaleGatewayLifecycleRequestSchema,
        CloverLifecycleFinalizationGatewayLifecycleRequestSchema,
        CloverLifecyclePreflightGatewayLifecycleRequestSchema,
        CloverReconciliationGatewayLifecycleRequestSchema,
        CloverRefundGatewayLifecycleRequestSchema,
        CloverStoredCardSaleGatewayLifecycleRequestSchema,
        CloverTerminalSaleGatewayLifecycleRequestSchema,
        CloverTerminalTokenizationGatewayLifecycleRequestSchema,
        CloverTransactionStatusGatewayLifecycleRequestSchema,
        CloverVoidGatewayLifecycleRequestSchema,
        IntegrationReconciliationGatewayLifecycleRequestSchema,
        TerminalSaleDispatchGatewayLifecycleRequestSchema,
        ValorCardTokenSaleGatewayLifecycleRequestSchema,
        ValorReconciliationGatewayLifecycleRequestSchema,
        ValorRefundGatewayLifecycleRequestSchema,
        ValorTerminalPublishGatewayLifecycleRequestV1Schema,
        ValorTerminalPublishGatewayLifecycleRequestV2Schema,
        ValorTransactionListGatewayLifecycleRequestSchema,
        ValorTransactionStatusGatewayLifecycleRequestSchema,
        ValorVoidGatewayLifecycleRequestSchema,
    ]);

export const StoredGatewayLifecycleRequestSchema:
    z.ZodType<GatewayLifecycleRequest> = z.preprocess(
        normalizeStoredGatewayLifecycleRequestNumericLeaves,
        GatewayLifecycleRequestSchema
    );

export function normalizeGatewayLifecycleRequestStorageV1(
    request: GatewayLifecycleRequest
): GatewayLifecycleRequest {
    return request;
}

export const GatewayLifecycleRequestStorageContract =
    defineStoredJsonContract({
        contractName: 'GatewayLifecycleRequest',
        semanticFieldValues: {},
        variants: {
            v1: {
                schemaName: 'StoredGatewayLifecycleRequestSchema',
                schema: StoredGatewayLifecycleRequestSchema,
                schemaInput: (boundary: StoredJsonRowBoundary) => ({
                    gateway_request_type:
                        boundary.siblingValues.gateway_request_type,
                    gateway_request: boundary.value,
                }),
                guards: [
                    {
                        kind: 'sibling_field_present',
                        siblingField: 'gateway_request_type',
                    },
                ],
                normalize: normalizeGatewayLifecycleRequestStorageV1,
                semanticPaths: {},
                unrepresentedSemanticFields: [],
            },
        },
        writerVariant: 'v1',
    });

export const StoredGatewayLifecycleRequestRowParser:
    z.ZodType<GatewayLifecycleRequest> =
    buildStoredJsonParser(GatewayLifecycleRequestStorageContract);

export function parseGatewayLifecycleRequestParts(params: {
    readonly requestType: string;
    readonly payload: unknown;
    readonly context: string;
}): GatewayLifecycleRequest {
    const parseResult = GatewayLifecycleRequestSchema.safeParse({
        gateway_request_type: params.requestType,
        gateway_request: params.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid gateway_request for ${params.context} type ${params.requestType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}

export function parseStoredGatewayLifecycleRequestParts(params: {
    readonly requestType: string;
    readonly payload: unknown;
    readonly context: string;
}): GatewayLifecycleRequest {
    const parseResult = StoredGatewayLifecycleRequestSchema.safeParse({
        gateway_request_type: params.requestType,
        gateway_request: params.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid gateway_request for ${params.context} type ${params.requestType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}
